// ⚠️ 自 2026-09-23 起，v2 的导航结构与旧站 frontend/src/config/roleMenus.tsx **分叉**：
//    v2 改为「业务域（一级，9 个）+ 页面（二级，单级不折叠）+ ready 迁移标记」，
//    旧站保持原「8 折叠分组」结构不变（旧站已冻结，仅做缺陷修复）。
//    两套前端自此不再共享同一组菜单用例；v2 的约定由本目录 roleMenus.test.ts 固化。
/**
 * roleMenus.tsx — 角色 → 导航（业务域 / 页面）映射（v2 导航唯一数据源）
 *
 * 结构：
 *   NAV_DOMAINS[] 业务域（一级）
 *     └─ pages[] 页面（二级，单级平铺，不再折叠）
 *
 * 关键约定（由 roleMenus.test.ts 固化）：
 *   · `roles: []` = 已登录即可见；空角色列表 → 空导航（未登录看不到任何菜单）
 *   · `adminOnly` = 仅「系统管理员」可见
 *   · `ready: false` = 尚未迁移到 v2 → **不在导航出现**；但路由仍可访问
 *     （深链落在「建设中」占位页，而不是 403 —— 见 getAllowedMenuKeys 注释）
 *   · 业务域内「角色可见且 ready」的页面数为 0 时，该域整体隐藏（不出现空域页签）
 *   · 导航形态按角色自适应：可见页面数 ≤ NAV_MODE_THRESHOLD（8）用**顶部页面页签**
 *     （参考工程 hr_dynimic_mgmt 的纯顶部形态，无侧栏）；超过阈值用
 *     「顶部业务域页签 + 侧栏当前域页面」，兼顾分类与可用页
 */

import type { ReactNode } from "react";
import { Icon } from "../components/icons/Icon";

export interface NavPage {
  /** 路由路径（外链项为 URL） */
  key: string;
  label: string;
  icon?: ReactNode;
  /** 可见角色（中文角色名）；空数组 = 已登录即可见 */
  roles: string[];
  /** true = 仅系统管理员可见 */
  adminOnly?: boolean;
  /** 是否已迁移到 v2（false 时不在导航显示） */
  ready: boolean;
  /** 外部链接（如 HR 门户），点击整页跳转 */
  externalUrl?: string;
}

export interface NavDomain {
  key: string;
  label: string;
  /** 顶部业务域页签用短名（≤4 字，避免页签过宽） */
  short: string;
  icon: ReactNode;
  pages: NavPage[];
}

/** 形态阈值：可见页面数 ≤ 该值时用顶部页面页签形态（无侧栏） */
export const NAV_MODE_THRESHOLD = 8;

export type NavMode = "tabs" | "domains";

const ROLE = {
  buyer: "采购员",
  warehouse: "仓管员",
  production: "生产主管",
  quality: "质检员",
  sales: "销售员",
  executive: "厂长",
  finance: "财务",
  equipment: "设备主管",
  operator: "操作员",
  pm: "项目经理",
  pmo: "PMO",
  boss: "老板",
} as const;

/**
 * 业务域划分（重构要点：原 `group-erp` 一个折叠组里混装了库存/盘点/扫码/采购/请购/报价/销售/财务，
 * 分组名与内容不符、找东西必须逐级展开 —— 现按业务职能拆为 9 个域）。
 */
export const NAV_DOMAINS: NavDomain[] = [
  {
    key: "domain-home",
    label: "经营驾驶舱",
    short: "驾驶舱",
    icon: <Icon name="dashboard" />,
    pages: [
      {
        key: "/",
        label: "综合驾驶舱",
        icon: <Icon name="dashboard" />,
        roles: [ROLE.buyer, ROLE.warehouse, ROLE.production, ROLE.quality, ROLE.sales, ROLE.executive, ROLE.finance, ROLE.equipment, ROLE.operator],
        ready: true,
      },
      { key: "/chatbi", label: "智能问数", icon: <Icon name="bar-chart" />, roles: [ROLE.production, ROLE.finance, ROLE.executive], ready: true },
    ],
  },
  {
    key: "domain-supply",
    label: "供应链",
    short: "供应链",
    icon: <Icon name="appstore" />,
    // 二级菜单按**单据时序**排（2026-09-26）：请购 → 采购 → 扫码出入库（收货执行）→ 库存 → 盘点。
    // 原先以「库存管理」打头，但真实业务链是「先请购、再采购、收货后才形成库存、盘点收尾」。
    pages: [
      // 请购管理：申请人（采购员/生产主管）提报与提交；**批准人（厂长/财务）也要能进来**，
      // 否则"请购单闭环"里的批准这一步在界面上根本无处可点（批次六十一）。
      { key: "/purchase-req", label: "请购管理", icon: <Icon name="form" />, roles: [ROLE.buyer, ROLE.production, ROLE.executive, ROLE.finance], ready: true },
      // 仓管员: receipt:read 收货前提是看采购单（供应商 tab 403 由页面容错）
      {
        key: "/purchase",
        label: "采购管理",
        icon: <Icon name="shopping-cart" />,
        roles: [ROLE.buyer, ROLE.sales, ROLE.finance, ROLE.executive, ROLE.warehouse],
        ready: true,
      },
      { key: "/mobile-scan", label: "扫码出入库", icon: <Icon name="scan" />, roles: [ROLE.warehouse], ready: true },
      // 生产主管: material:read 可选料，库存页物料列表可用（余额/流水 403 由页面容错）
      {
        key: "/inventory",
        label: "库存管理",
        icon: <Icon name="inbox" />,
        roles: [ROLE.buyer, ROLE.warehouse, ROLE.quality, ROLE.sales, ROLE.executive, ROLE.finance, ROLE.equipment, ROLE.operator, ROLE.production],
        ready: true,
      },
      { key: "/stockcheck", label: "盘点管理", icon: <Icon name="check-square" />, roles: [ROLE.warehouse, ROLE.executive], ready: true },
    ],
  },
  {
    key: "domain-sales",
    label: "销售与客户",
    short: "销售",
    icon: <Icon name="trophy" />,
    // 二级菜单按**业务时序**排（2026-09-26：原先顺序是 订单→CRM→客户→报价，看起来像随手加的）：
    //   线索/商机（售前）→ 客户转化 → 报价 → 订单。报价必须紧邻订单之前 —— 报价通过就转订单，
    //   这是销售员最常走的顺序（也是本次调整的直接动因）。
    pages: [
      { key: "/sales-crm", label: "销售CRM", icon: <Icon name="trophy" />, roles: [ROLE.sales, ROLE.executive], ready: true },
      { key: "/customer", label: "客户管理", icon: <Icon name="team" />, roles: [ROLE.sales, ROLE.finance], ready: true },
      // 报价单走的是销售侧接口（/api/v1/sales/quotes，需 sales:read）：此前把该页也给了采购员，
      // 结果他一进就是 403（页面可见、接口拒绝）。采购要的价格本在「采购管理 → 价格本」页签，不在销售报价。
      { key: "/pricing", label: "报价管理", icon: <Icon name="percentage" />, roles: [ROLE.sales], ready: true },
      { key: "/sales", label: "销售订单", icon: <Icon name="shop" />, roles: [ROLE.sales, ROLE.finance, ROLE.executive], ready: true },
    ],
  },
  {
    key: "domain-manufacturing",
    label: "生产制造",
    short: "制造",
    icon: <Icon name="deployment-unit" />,
    // 二级菜单按**开工前 → 执行中 → 持续监控**排（2026-09-26）：计划 → 指导书/上料校验 → 报工 → 异常 → 设备。
    // 原先「设备监控/安灯」夹在计划与执行之间，与现场动线不符（先排产、开工前对 SOP 与上料，再报工）。
    pages: [
      { key: "/production", label: "生产计划", icon: <Icon name="schedule" />, roles: [ROLE.production, ROLE.equipment, ROLE.operator, ROLE.executive], ready: true },
      { key: "/sop", label: "SOP作业指导", icon: <Icon name="file-text" />, roles: [ROLE.production, ROLE.equipment, ROLE.operator, ROLE.executive], ready: true },
      { key: "/material-verify", label: "上料防错", icon: <Icon name="check-square" />, roles: [ROLE.production, ROLE.operator], ready: true },
      { key: "/workshop", label: "车间执行", icon: <Icon name="tool" />, roles: [ROLE.production, ROLE.operator, ROLE.executive], ready: true },
      { key: "/andon", label: "安灯管理", icon: <Icon name="alert" />, roles: [ROLE.production, ROLE.equipment, ROLE.operator], ready: true },
      { key: "/equipment", label: "设备监控", icon: <Icon name="dashboard" />, roles: [ROLE.production, ROLE.equipment, ROLE.operator, ROLE.executive], ready: true },
    ],
  },
  {
    key: "domain-quality",
    label: "质量与追溯",
    short: "质量",
    icon: <Icon name="safety" />,
    pages: [
      { key: "/quality", label: "质量管理", icon: <Icon name="safety" />, roles: [ROLE.quality, ROLE.executive], ready: true },
      { key: "/traceability", label: "追溯管理", icon: <Icon name="node-index" />, roles: [ROLE.warehouse, ROLE.quality, ROLE.executive], ready: true },
    ],
  },
  {
    key: "domain-engineering",
    label: "工程数据",
    short: "工程",
    icon: <Icon name="partition" />,
    // 二级菜单：编码规则（先定物料/成品怎么编号）→ BOM（再用这些物料组装）。2026-09-26 调整。
    pages: [
      { key: "/bom-coder-rules", label: "编码规则", icon: <Icon name="code" />, roles: [ROLE.production], ready: true },
      { key: "/bom", label: "BOM 管理", icon: <Icon name="build" />, roles: [ROLE.production, ROLE.executive], ready: true },
    ],
  },
  {
    key: "domain-finance",
    label: "财务",
    short: "财务",
    icon: <Icon name="dollar" />,
    // 独立成域（而非并入「供应链」）：财务的职责与权限（财务/厂长）与采购库存完全不同，
    // 合并会让域名与内容不符 —— 正是本次重构要修的「分组名与内容不匹配」问题。
    pages: [{ key: "/finance", label: "财务管理", icon: <Icon name="dollar" />, roles: [ROLE.finance, ROLE.executive], ready: true }],
  },
  {
    key: "domain-project",
    label: "项目管理",
    short: "项目",
    icon: <Icon name="schedule" />,
    // 2026-09-25：PM/PMO 此前在 v2 **没有任何页面** —— 只能在工作区看项目指标，点不进明细
    //（项目/里程碑/任务只能在 MCP 或接口层操作）。行级可见由服务端裁定：pm 只见本人项目，
    // PMO / 老板 / 厂长全量，故此处给这四类角色。该域对其它角色整体隐藏（无可见页面）。
    pages: [
      { key: "/project", label: "项目管理", icon: <Icon name="schedule" />, roles: [ROLE.pm, ROLE.pmo, ROLE.executive, ROLE.boss], ready: true },
    ],
  },
  {
    key: "domain-collab",
    label: "协同办公",
    short: "协同",
    icon: <Icon name="team" />,
    // 二级菜单按**日常使用顺序**排（2026-09-26）：个人门户 → 待办审批 → 消息 → 提醒配置 → 反馈 → 管理员项。
    // 原先「我的工作区」排在第 4 位（在提醒配置之后），但它是进入协同的第一落点，应在最前。
    pages: [
      {
        key: "/workspace",
        label: "我的工作区",
        icon: <Icon name="dashboard" />,
        roles: [ROLE.buyer, ROLE.sales, ROLE.finance, ROLE.executive, ROLE.pm, ROLE.pmo, ROLE.boss, ROLE.production],
        ready: true,
      },
      // D7: 仅持有 approval:read 的角色可见（销售/设备/操作员/仓管/质检无审批权限 → 隐藏）
      { key: "/approval", label: "审批工作台", icon: <Icon name="audit" />, roles: [ROLE.buyer, ROLE.production, ROLE.finance, ROLE.executive], ready: true },
      {
        key: "/notifications",
        label: "消息通知",
        icon: <Icon name="message" />,
        roles: [ROLE.buyer, ROLE.warehouse, ROLE.production, ROLE.quality, ROLE.sales, ROLE.executive, ROLE.finance, ROLE.equipment, ROLE.operator],
        ready: true,
      },
      { key: "/reminder", label: "提醒配置", icon: <Icon name="bell" />, roles: [ROLE.executive], ready: true },
      { key: "/feedback", label: "问题反馈", icon: <Icon name="bulb" />, roles: [], ready: true },
      { key: "/workspace-admin", label: "模板管理", icon: <Icon name="appstore" />, roles: [], adminOnly: true, ready: true },
    ],
  },
  {
    key: "domain-governance",
    label: "治理后台",
    short: "治理",
    icon: <Icon name="setting" />,
    pages: [
      { key: "/iam", label: "组织权限", icon: <Icon name="team" />, roles: [], adminOnly: true, ready: true },
      { key: "/ai-config", label: "系统设置", icon: <Icon name="setting" />, roles: [], adminOnly: true, ready: true },
      { key: "/monitoring", label: "前端监控", icon: <Icon name="dashboard" />, roles: [], adminOnly: true, ready: true },
    ],
  },
];

/** HR 门户：独立部署的外链项（不属于任何业务域，在所有形态下都作为独立入口渲染） */
export const HR_PORTAL_PAGE: NavPage = {
  key: "hr-portal",
  label: "人力资源",
  icon: <Icon name="team" />,
  roles: [],
  ready: true,
  externalUrl: "https://hr.co-seek.cn",
};

/** 全部页面（含未 ready），用于 key 查找与「深链不 403」判定 */
export const ALL_PAGES: NavPage[] = NAV_DOMAINS.flatMap((domain) => domain.pages);

// ── 可见性 ──

export function isAdmin(roleNames: string[]): boolean {
  return roleNames.includes("系统管理员");
}

function canSeePage(page: { roles?: string[]; adminOnly?: boolean }, roleNames: string[]): boolean {
  if (page.adminOnly) return isAdmin(roleNames);
  if (!page.roles || page.roles.length === 0) return true;
  return page.roles.some((role) => roleNames.includes(role));
}

/** 当前角色可见的页面（仅 ready，且按配置顺序；管理员全通） */
export function getVisiblePages(roleNames: string[]): NavPage[] {
  if (roleNames.length === 0) return [];
  const readyPages = ALL_PAGES.filter((page) => page.ready && !page.externalUrl);
  if (isAdmin(roleNames)) return readyPages;
  return readyPages.filter((page) => canSeePage(page, roleNames));
}

/** 当前角色可见的业务域（域内可见页面为 0 的域整体隐藏；管理员全通） */
export function getVisibleDomains(roleNames: string[]): NavDomain[] {
  if (roleNames.length === 0) return [];
  const admin = isAdmin(roleNames);
  return NAV_DOMAINS.map((domain) => ({
    ...domain,
    pages: domain.pages.filter((page) => page.ready && !page.externalUrl && (admin || canSeePage(page, roleNames))),
  })).filter((domain) => domain.pages.length > 0);
}

/** 导航形态判定：可见页面少（≤阈值）时用顶部页面页签，否则用业务域页签 + 侧栏 */
export function resolveNavMode(visiblePageCount: number, threshold = NAV_MODE_THRESHOLD): NavMode {
  return visiblePageCount <= threshold ? "tabs" : "domains";
}

/**
 * 当前角色允许访问的页面 key 集合。
 * 注意：**包含未 ready 的页面** —— 未 ready 只是「不在导航出现」，
 * 深链/书签访问时仍应落在「建设中」占位页，而不是被判 403（未迁移 ≠ 无权限）。
 */
export function getAllowedMenuKeys(roleNames: string[]): Set<string> {
  if (roleNames.length === 0) return new Set<string>();
  if (isAdmin(roleNames)) return new Set<string>([...ALL_PAGES.map((page) => page.key), HR_PORTAL_PAGE.key]);
  const allowed = new Set<string>();
  ALL_PAGES.forEach((page) => {
    if (canSeePage(page, roleNames)) allowed.add(page.key);
  });
  if (canSeePage(HR_PORTAL_PAGE, roleNames)) allowed.add(HR_PORTAL_PAGE.key);
  return allowed;
}

/** 当前用户能否访问指定 pathname（精确或子路径匹配，避免 /bom 误匹配 /bom-coder-rules） */
export function canAccessRoute(roleNames: string[], pathname: string): boolean {
  if (isAdmin(roleNames)) return true;
  if (roleNames.length === 0) return false;
  const allowed = getAllowedMenuKeys(roleNames);
  if (pathname === "/") return allowed.has("/");
  for (const key of allowed) {
    if (key === "/") continue;
    if (pathname === key || pathname.startsWith(key + "/")) return true;
  }
  return false;
}

/** 当前用户是否可见审批工作台（含 admin；用于角标/待办卡片的显示开关） */
export function canAccessApproval(roleNames: string[]): boolean {
  return getAllowedMenuKeys(roleNames).has("/approval");
}

/**
 * D7-2: 模块读权判定（与后端 IAM 矩阵 + routeIAMPermissions 对齐）。
 * 用于聚合统计卡片「有权才请求、才渲染」，杜绝无权模块 403 噪声。
 * resource 取值: material / po / equipment / supplier / iam / kpi / fin / sales / stock ...
 */
const MODULE_READ_ROLES: Record<string, string[]> = {
  material: ["采购员", "仓管员", "质检员", "销售员", "厂长", "财务", "设备主管", "操作员", "生产主管"], // material:read
  po: ["采购员", "销售员", "财务", "厂长", "质检员", "仓管员"], // po:read（质检 D13）或 receipt:read（仓管 D4）
  equipment: [ROLE.production, ROLE.equipment, ROLE.operator, ROLE.executive], // equipment:read
  supplier: [ROLE.buyer, ROLE.sales, ROLE.executive], // supplier:read
  iam: [], // iam:read 仅 admin（role-admin 全通）
  kpi: [ROLE.production, ROLE.finance, ROLE.executive], // kpi:read
  fin: [ROLE.finance, ROLE.executive], // fin:read
  sales: [ROLE.sales, ROLE.finance, ROLE.executive], // sales:read
  stock: [ROLE.warehouse, ROLE.executive], // stock:read（D7-2 审计校准）
  movement: [ROLE.warehouse, ROLE.executive], // movement:read
  customers: [ROLE.sales, ROLE.finance, ROLE.executive], // customers:read（N1: 销售员读写、财务/厂长只读）
  project: [ROLE.pm, ROLE.pmo, ROLE.boss, ROLE.sales, ROLE.executive], // project:read（PM 行级在服务层按 pm_id）
};

export function canReadModule(roleNames: string[], resource: string): boolean {
  if (isAdmin(roleNames)) return true;
  const allowed = MODULE_READ_ROLES[resource];
  if (!allowed) return false; // 未映射资源默认拒绝（fail-closed）
  return allowed.some((role) => roleNames.includes(role));
}

// ── 查找与记忆 ──

/** 页面的中文标签（顶栏标题、占位页标题用） */
export function getMenuLabel(key: string): string {
  const page = ALL_PAGES.find((item) => item.key === key);
  if (page) return page.label;
  if (key === HR_PORTAL_PAGE.key) return HR_PORTAL_PAGE.label;
  return "综合驾驶舱";
}

/** 按路径找页面（先精确、再最长前缀，避免 /bom 误匹配 /bom-coder-rules） */
export function findPageByPath(pathname: string): NavPage | undefined {
  const exact = ALL_PAGES.find((page) => page.key === pathname);
  if (exact) return exact;
  return ALL_PAGES.filter((page) => page.key !== "/" && pathname.startsWith(page.key + "/")).sort((a, b) => b.key.length - a.key.length)[0];
}

/** 路径所属业务域（用于记忆与高亮） */
export function findDomainByPath(pathname: string, domains: NavDomain[] = NAV_DOMAINS): NavDomain | undefined {
  const page = findPageByPath(pathname);
  if (page) {
    const owner = domains.find((domain) => domain.pages.some((item) => item.key === page.key));
    if (owner) return owner;
  }
  return domains.find((domain) => domain.key === loadActiveDomain()) ?? domains[0];
}

const NAV_DOMAIN_KEY = "v2_nav_domain";

/** 记忆「上次停留的业务域」（domains 形态下切页/刷新后仍停在同一个域） */
export function loadActiveDomain(): string | null {
  try {
    return localStorage.getItem(NAV_DOMAIN_KEY);
  } catch {
    return null;
  }
}

export function saveActiveDomain(domainKey: string): void {
  try {
    localStorage.setItem(NAV_DOMAIN_KEY, domainKey);
  } catch {
    // 隐私模式下 localStorage 不可用：忽略，不影响导航
  }
}
